import { ReactNode } from 'react';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { TypedTypePolicies } from '@/graphql/generated';
import { useAuthHeaders } from '@/hooks/useAuthHeaders';
import { useTranslation } from '@/hooks/useTranslation';

export function ApiApolloProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const getHeaders = useAuthHeaders();

  const httpLink = new HttpLink({ uri: `${process.env.API_URL}/graphql` });

  const authLink = setContext((_, { headers }) => {
    return getHeaders().then((authHeaders) => {
      return {
        headers: {
          'accept-language': i18n.language ?? 'fr',
          ...authHeaders,
          ...headers,
        },
      };
    });
  });

  // Remove '__typename' from variables
  const cleanTypeName = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      const omitTypename = (key: string, value: string) =>
        key === '__typename' ? undefined : value;
      operation.variables = JSON.parse(
        JSON.stringify(operation.variables),
        omitTypename,
      );
    }
    return forward(operation).map((data) => {
      return data;
    });
  });
  const typePolicies: TypedTypePolicies = {
    Survey: {
      fields: {
        recipients: {
          merge(existing, incoming) {
            return incoming;
          },
        },
        respondents: {
          merge(existing, incoming) {
            return incoming;
          },
        },
      },
    },
    DiagnosticStake: {
      fields: {
        computedImpactMaterialityScore: {
          keyArgs: ['mode'],
          // Always use the incoming value (fresh from server) when merging
          // This ensures refetchQueries updates the cache correctly
          merge(existing, incoming) {
            return incoming;
          },
        },
        computedFinancialMaterialityScore: {
          keyArgs: ['mode'],
          // Always use the incoming value (fresh from server) when merging
          // This ensures refetchQueries updates the cache correctly
          merge(existing, incoming) {
            return incoming;
          },
        },
      },
    },
  };

  const apolloClient = new ApolloClient({
    cache: new InMemoryCache({ typePolicies }),
    link: ApolloLink.from([cleanTypeName, authLink, httpLink]),
    devtools: {
      enabled: process.env.NODE_ENV !== 'production',
    },
  });

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}
